import {
  clampHorizontalScrollLeft,
  getMaxScrollLeft,
  HORIZONTAL_SCROLL_VISUAL_EDGE_TOLERANCE,
  isAtHorizontalScrollEnd,
  isAtHorizontalScrollStart,
  isAtPhysicalMaxScroll
} from '../../utils/horizontalScrollEdge';

import type { LerpScrollHandle } from './lerpScroll';
import { ScrollZone } from './types';

export type CreateWheelHandlerOptions = {
  zone: ScrollZone;
  scrollbarZoneHeight: number;
  lerp: LerpScrollHandle;
};

export const createWheelHandler = (
  container: HTMLElement,
  { zone, scrollbarZoneHeight, lerp }: CreateWheelHandlerOptions
): ((evt: WheelEvent) => void) => {
  const getScrollMetrics = () => ({
    scrollWidth: container.scrollWidth,
    clientWidth: container.clientWidth
  });

  return (evt: WheelEvent) => {
    if (Math.abs(evt.deltaX) >= Math.abs(evt.deltaY)) {
      lerp.setTargetScrollLeft(container.scrollLeft);
      lerp.cancelAnimation();
      return;
    }

    const { scrollWidth, clientWidth } = getScrollMetrics();
    const maxScrollLeft = getMaxScrollLeft(scrollWidth, clientWidth);

    if (maxScrollLeft <= 0) {
      return;
    }

    if (zone === ScrollZone.scrollbar) {
      const rect = container.getBoundingClientRect();
      const distanceFromBottom = rect.bottom - evt.clientY;

      if (distanceFromBottom > scrollbarZoneHeight || distanceFromBottom < -2) {
        return;
      }
    }

    const scrollingTowardEnd = evt.deltaY > 0;
    const scrollingTowardStart = evt.deltaY < 0;

    if (scrollingTowardEnd) {
      const atEnd = isAtHorizontalScrollEnd(
        container.scrollLeft,
        scrollWidth,
        clientWidth
      );

      if (atEnd) {
        lerp.cancelAnimation();
        lerp.setTargetScrollLeft(container.scrollLeft);
        return;
      }

      const nearMax =
        container.scrollLeft >=
        maxScrollLeft - HORIZONTAL_SCROLL_VISUAL_EDGE_TOLERANCE;

      if (nearMax && isAtPhysicalMaxScroll(container)) {
        lerp.cancelAnimation();
        lerp.setTargetScrollLeft(container.scrollLeft);
        return;
      }
    }

    if (
      scrollingTowardStart &&
      isAtHorizontalScrollStart(container.scrollLeft)
    ) {
      lerp.cancelAnimation();
      return;
    }

    evt.preventDefault();

    lerp.syncTargetFromContainer();

    lerp.setTargetScrollLeft(
      clampHorizontalScrollLeft(
        lerp.getTargetScrollLeft() + evt.deltaY,
        scrollWidth,
        clientWidth
      )
    );

    lerp.scheduleLerp();
  };
};
